import React from 'react';
import { Button, Empty, List, Skeleton, Space, Tag, Typography } from 'antd';
import { CarOutlined, FileTextOutlined, FilePdfOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import FindingsStrip from './FindingsStrip';

const { Text } = Typography;

/**
 * Rows for the document library.
 */
const DocumentList = ({ documents = [], loading = false, onSelect, onEvent, emptyText }) => {
  if (loading) {
    return <Skeleton active />;
  }

  if (documents.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={emptyText ?? 'No documents yet.'}
      />
    );
  }

  return (
    <List
      className="document-list"
      dataSource={documents}
      renderItem={(document) => (
        <List.Item
          className="document-list__item"
          onClick={() => onSelect?.(document)}
          role="button"
        >
          <List.Item.Meta
            avatar={<FileTextOutlined className="document-list__icon" />}
            title={
              <span className="document-list__title">
                {document.reference ? `${document.reference} — ` : ''}
                {document.name}
              </span>
            }
            description={
              <Space direction="vertical" size={2} className="document-list__meta">
                <Text type="secondary">
                  {[document.templateName, document.companyName].filter(Boolean).join(' · ')}
                </Text>
                <Space size={6} wrap>
                  <Tag color={document.isComplete ? 'green' : 'orange'}>
                    {document.currentStatusName ?? (document.isComplete ? 'Complete' : 'In Progress')}
                  </Tag>
                  {document.createdDateTime && (
                    <Text type="secondary">
                      {dayjs(document.createdDateTime).format('DD MMM YYYY')}
                    </Text>
                  )}
                </Space>
              </Space>
            }
          />

          <div className="document-list__actions">
            {/*
              Commented out View PDF Button until implemented properly

            {document.isComplete && onViewPdf && (
              <Button
                size="small"
                icon={<FilePdfOutlined />}
                onClick={(event) => {
                  // Without this the row's own handler also fires and the
                  // editor opens behind the PDF.
                  event.stopPropagation();
                  onViewPdf(document);
                }}
              >
                View
              </Button>
            )}
            */}

            {onEvent && document.eventId && (
              <Button
                size="small"
                icon={<CarOutlined />}
                onClick={(event) => {
                  event.stopPropagation();
                  onEvent(document);
                }}
              >
                {document.eventReference || 'Event'}
              </Button>
            )}

            {/* Same rule the API enforces: an AssetPicker for the vehicle, and
                something red or amber worth raising an event about. */}
            {onEvent
              && !document.eventId
              && document.isComplete
              && document.canConvertToEvent
              && (document.redCount ?? 0) + (document.amberCount ?? 0) > 0 && (
              <Button
                size="small"
                type="primary"
                icon={<CarOutlined />}
                onClick={(event) => {
                  event.stopPropagation();
                  onEvent(document);
                }}
              >
                Event
              </Button>
            )}

            {document.hasRating && (
              <div className="document-list__findings">
                <FindingsStrip
                  variant="circles"
                  counts={{
                    red: document.redCount ?? 0,
                    amber: document.amberCount ?? 0,
                    green: document.greenCount ?? 0,
                  }}
                />
              </div>
            )}
          </div>
        </List.Item>
      )}
    />
  );
};

export default DocumentList;
